import { timezones } from '@edicom/common/date/timezones';
import { compare } from '@edicom/common/object/compare';
import { clone } from '@edicom/common/object/clone';

import { translations } from './src/translation/translations';
import { translate } from './src/translation/translate';
import { translateTo } from './src/translation/translateTo';
import { DATEFORMATS } from './src/date/DATEFORMATS';
import { formatFn } from './src/date/formatFn';
import { formatters } from './src/date/formatters';
import { humanizeFn } from './src/date/humanizeFn';
import { humanizers } from './src/date/humanizers';
import { parse as dateParser } from './src/date/parse';
import { Tz } from './src/date/tz';
import { ago } from './src/date/ago';
import { utc } from './src/date/utc';
import { getPattern } from './src/date/getPattern';
import { number, percent, compact, currency, unit, fileSize } from './src/number/numberFormat';

const state = {
  id: '', // podría ser: navigator.language
  lang: '', // podría ser: navigator.language.substring(0,2)
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  dictionary: {},
  dateFormat: DATEFORMATS['localized'], // default frontend sería 'dd/MM/yyyy HH:mm:ss'
};
const dispatch = eventName => {
  if (globalThis.document) {
    globalThis.document.dispatchEvent(new CustomEvent(`Locale:${eventName}`, { bubbles: true }));
  }
};

const DEFAULT_TIMEZONE = 'Europe/Madrid';

// exponemos "locale" como servicio en la variable global "edicom"

/**
 * @typedef locale
 * @property {string} id el id de la locale, ej 'es', o 'en-US' (2 o 5 caracteres con '-')
 * @property {string} lang el idioma, ej 'es', o 'en' (2 letras en minusculas)
 * @property {string} timezone el timezone
 */
export const edicomLocale = {
  // @property {string} id
  // @property {string} lang
  // @property {string} timezone

  // init del objeto
  getLocale,
  setLocale,
  setFallback,
  getLang,
  setLang,
  getTimezone,
  setTimezone,
  setDateFormat,
  getDateFormat,
  getDateFormats,

  // metodos de traducciones
  translations: translations.bind(null, state),
  translate: translate.bind(null, state),
  translateTo: translateTo.bind(null, state),

  // metodos de formateo fechas
  format: formatters.format.bind(null, state, formatFn),
  datetime: formatters.datetime.bind(null, state, formatFn),
  datetimelong: formatters.datetimelong.bind(null, state, formatFn),
  date: formatters.date.bind(null, state, formatFn),
  time: formatters.time.bind(null, state, formatFn),
  timelong: formatters.timelong.bind(null, state, formatFn),

  humanizeDatetime: humanizers.datetime.bind(null, state, humanizeFn),
  humanizeDatetimelong: humanizers.datetimelong.bind(null, state, humanizeFn),
  humanizeDate: humanizers.date.bind(null, state, humanizeFn),
  humanizeTime: humanizers.time.bind(null, state, humanizeFn),
  humanizeTimelong: humanizers.timelong.bind(null, state, humanizeFn),

  parse: dateParser.bind(null),

  tz: Tz(state),
  ago: ago.bind(null, state),
  utc: utc.bind(null, state),

  getPattern: getPattern.bind(null, state),

  // formateo de numbers
  number: number.bind(null, state),
  percent: percent.bind(null, state),
  compact: compact.bind(null, state),
  currency: currency.bind(null, state),
  unit: unit.bind(null, state),
  fileSize: fileSize.bind(null, state),
};

Object.defineProperties(edicomLocale, {
  id: {
    get: () => getLocale(),
    set: value => {
      setLocale(value);
    },
  },
  lang: {
    get: () => getLang(),
    set: value => {
      setLang(value);
    },
  },
  timezone: {
    get: () => getTimezone(),
    set: value => {
      setTimezone(value);
    },
  },
});

//

/**
 * Devuelve la locale actual.
 *
 * @returns {string} la locale ej. "en-US" o "es"
 */
function getLocale() {
  return state.id;
}

/**
 * Permite registrar la nueva locale.
 *
 * @param {string} id la nueva locale, ej. "en-US" o "es"
 * @param {string} [lang] OPCIOANL. El idioma. Solo si queremos sobreescribir el idioma de la locale. Solo debería servir para el EdiwinFrontend...
 */
function setLocale(id, lang = '') {
  if (typeof id !== 'string' || id.length < 2 || id.length > 6) {
    throw new Error(`Format invalid for locale: "${id}". Must be a string between 2 and 6 characters: 'es', 'en-US', 'es-419' (UN M49)`);
  }

  // limpiamos el id:
  id = id.replace('_', '-');

  // actualizamos el `lang`
  lang = lang || id.substring(0, 2);
  if (typeof lang === 'string' && lang.length === 2) {
    setLang(lang);
  }

  // actualizamos solo si es distinto
  if (state.id !== id) {
    state.id = id;
    dispatch('id');
    return true;
  }

  return false;
}

/**
 * Permite registrar un nuevo fallback.
 *
 * @param {string} fallback el nuevo fallback, ej. "en-US" o "es"
 */
function setFallback(fallback) {
  // actualizamos solo si es distinto
  if (state.fallback !== fallback) {
    state.fallback = fallback;
    dispatch('fallback');
    return true;
  }

  return false;
}

/**
 * Devuelve el idioma actual.
 *
 * @returns {string} 2 caracteres en minusculas
 */
function getLang() {
  return state.lang;
}

/**
 * Permite registrar el idioma (solo sirve si distinto de la locale, solo debería usarse en el EdiwinFrontend...)
 * @param {string} lang el idioma (2 caracteres en minusculas)
 */
function setLang(lang) {
  if (typeof lang !== 'string' || lang.length !== 2) {
    throw new Error(`Format invalid for lang: "${lang}". Must be a string of 2 caracters. ej 'es', o 'en'`);
  }

  // limpiamos el lang:
  lang = lang.toLowerCase();

  if (state.lang !== lang) {
    state.lang = lang;
    dispatch('lang');
    return true;
  }

  return false;
}

/**
 * Devuelve el timezone registrado.
 *
 * @returns {string}
 */
function getTimezone() {
  return state.timezone;
}

/**
 * Permite registrar el timezone.
 * En caso de que el timezone pasado no coincida con la lista de timezones, pondremos el timezone por defecto
 * y sacaremos el problema en un console.error.
 *
 * @param {string} timezone el nuevo timezone
 */
function setTimezone(timezone) {
  if (typeof timezone !== 'string') {
    throw new Error(`Format invalid for timezone: "${timezone}". Must be a string`);
  }
  if (!timezones.includes(timezone)) {
    console.error(`Format invalid for timezone: "${timezone}". Must exist in list: ${timezones.sort()}`);
    timezone = 'Europe/Madrid';
  }

  if (state.timezone !== timezone) {
    state.timezone = timezone;
    dispatch('timezone');
    return true;
  }

  return false;
}

/**
 * Permite registrar el formato de fecha del usuario/dominio.
 *
 * @param {string} dateFormat el formato de fecha del usuario
 */
function setDateFormat(dateFormat) {
  if (!DATEFORMATS[dateFormat]) {
    throw new Error(`El dateFormat:"${dateFormat}" no existe. Seleccionar uno de esos: ${Object.keys(DATEFORMATS).join(', ')}`);
  }

  if (!compare(state.dateFormat, DATEFORMATS[dateFormat])) {
    state.dateFormat = clone(DATEFORMATS[dateFormat]);
    dispatch('dateFormat');
    return true;
  }

  return false;
}

/**
 * Devuelve el dateFormat actual.
 * No es una copia, entonces
 * permite modificarlo para cambiar los formatos (no recomendado).
 *
 * @returns {string}
 */
function getDateFormat() {
  return state.dateFormat;
}

/**
 * Devuelve un clone del objeto de configuracion DATEFORMATS.
 * @returns {string}
 */
function getDateFormats() {
  return clone(DATEFORMATS);
}
